import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrganizationContacts, useOrganizationProperties, useOrganizationClaims } from "@/hooks/use-organizations";
import { LinkEntityPopover, LinkedEntityRow } from "../LinkEntityPopover";
import type { LinkableEntity } from "../LinkEntityPopover";
import { navigateDirectory } from "../navigate";
import { apiClient } from "@/lib/api-client";
import { Building2, Phone, Mail, Globe, MapPin, User, FileText, Tag, StickyNote } from "lucide-react";
import { useLocation } from "wouter";
import type { Organization } from "@/api/generated/client/models";

interface OrganizationDrawerProps {
    organization: Organization | null;
    open: boolean;
    onClose: () => void;
    onEdit: (org: Organization) => void;
}

export function OrganizationDrawer({ organization, open, onClose, onEdit }: OrganizationDrawerProps) {
    const [, setLocation] = useLocation();
    const { contacts, isLoading, linkContact, unlinkContact } = useOrganizationContacts(organization?.id ?? null);
    const { properties, isLoading: propsLoading, linkProperty, unlinkProperty } = useOrganizationProperties(organization?.id ?? null);
    const { organizationClaims, isLoading: claimsLoading, linkClaim, unlinkClaim } = useOrganizationClaims(organization?.id ?? null, organization?.orgType);

    const searchContacts = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.contacts.listContacts({ search: query || undefined, limit: 15 });
        return (res.contacts || []).map((c) => ({ id: c.id, label: `${c.firstName} ${c.lastName}`, badge: c.contactType }));
    }, []);

    const searchProperties = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.properties.listProperties({ search: query || undefined, limit: 15 });
        return (res.properties || []).map((p) => ({
            id: p.id,
            label: [p.streetAddress, p.unit, p.city, p.state, p.zipCode].filter(Boolean).join(", "),
        }));
    }, []);

    const searchClaims = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.claims.listClaims({ search: query || undefined, limit: 15 });
        return (res.claims || [])
            // Only allow linking claims the user can edit; read-only shares are hidden.
            .filter((c) => (c.canWrite ?? false))
            .map((c) => ({
                id: c.id,
                label: [c.insuredName, c.claimNumber].filter(Boolean).join(" – ") || `Claim #${c.id}`,
            }));
    }, []);

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent side="right" className="overflow-y-auto overflow-x-hidden sm:max-w-lg">
                {organization && (
                    <>
                        <SheetHeader className="pb-4">
                            <SheetTitle className="text-xl flex items-center gap-2 break-words min-w-0">
                                <Building2 className="h-5 w-5 text-muted-foreground shrink-0" />
                                <span className="break-words min-w-0">{organization.name}</span>
                            </SheetTitle>
                            <SheetDescription className="flex items-center gap-2 flex-wrap">
                                {organization.orgType && (
                                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                        <Tag className="h-3.5 w-3.5 shrink-0" />{organization.orgType}
                                    </span>
                                )}
                            </SheetDescription>
                        </SheetHeader>

                        {/* Organization Info */}
                        <div className="space-y-3 pb-4">
                            {organization.phone && (
                                <div className="flex items-start gap-2 text-sm min-w-0">
                                    <Phone className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="break-words min-w-0">{organization.phone}</span>
                                </div>
                            )}
                            {organization.email && (
                                <div className="flex items-start gap-2 text-sm min-w-0">
                                    <Mail className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="break-words min-w-0">{organization.email}</span>
                                </div>
                            )}
                            {organization.website && (
                                <div className="flex items-start gap-2 text-sm min-w-0">
                                    <Globe className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    {/^https?:\/\//i.test(organization.website) ? (
                                        <a href={organization.website} target="_blank" rel="noopener noreferrer" className="text-primary underline break-words min-w-0">
                                            {organization.website}
                                        </a>
                                    ) : (
                                        <span className="break-words min-w-0">{organization.website}</span>
                                    )}
                                </div>
                            )}
                            {organization.address && (
                                <div className="flex items-start gap-2 text-sm min-w-0">
                                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="break-words min-w-0">{organization.address}</span>
                                </div>
                            )}
                            {organization.notes && (
                                <div className="flex items-start gap-2 text-sm min-w-0">
                                    <StickyNote className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="break-words min-w-0">{organization.notes}</span>
                                </div>
                            )}
                        </div>

                        <Button variant="outline" size="sm" onClick={() => onEdit(organization)}>
                            Edit Organization
                        </Button>

                        <Separator className="my-6" />

                        {/* Linked Contacts */}
                        <div>
                            <div className="flex items-center justify-between flex-wrap mb-3">
                                <h4 className="text-sm font-semibold flex items-center gap-2">
                                    <User className="h-4 w-4" /> Contacts ({contacts.length})
                                </h4>
                                <LinkEntityPopover
                                    existingIds={contacts.map((c) => c.id)}
                                    onSearch={searchContacts}
                                    onSelect={(id) => linkContact({ contactId: id })}
                                    placeholder="Search contacts..."
                                    emptyMessage="No matching contacts"
                                    noItemsMessage="No contacts yet"
                                    icon={User}
                                />
                            </div>
                            {isLoading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-8 w-full" />
                                    <Skeleton className="h-8 w-full" />
                                </div>
                            ) : contacts.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No contacts linked to this organization.</p>
                            ) : (
                                <div className="space-y-2">
                                    {contacts.map((c) => (
                                        <LinkedEntityRow
                                            key={c.id}
                                            icon={User}
                                            label={`${c.firstName} ${c.lastName}`}
                                            onNavigate={() => { onClose(); navigateDirectory("contacts", c.id); }}
                                            onUnlink={() => unlinkContact(c.id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <Separator className="my-6" />

                        {/* Linked Properties */}
                        <div>
                            <div className="flex items-center justify-between flex-wrap mb-3">
                                <h4 className="text-sm font-semibold flex items-center gap-2">
                                    <MapPin className="h-4 w-4" /> Properties ({properties.length})
                                </h4>
                                <LinkEntityPopover
                                    existingIds={properties.map((p) => p.id)}
                                    onSearch={searchProperties}
                                    onSelect={(id) => linkProperty(id)}
                                    placeholder="Search properties..."
                                    emptyMessage="No matching properties"
                                    noItemsMessage="No properties yet"
                                    icon={MapPin}
                                />
                            </div>
                            {propsLoading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-8 w-full" />
                                    <Skeleton className="h-8 w-full" />
                                </div>
                            ) : properties.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No properties linked to this organization.</p>
                            ) : (
                                <div className="space-y-2">
                                    {properties.map((p) => (
                                        <LinkedEntityRow
                                            key={p.id}
                                            icon={MapPin}
                                            label={[p.streetAddress, p.unit, p.city, p.state, p.zipCode].filter(Boolean).join(", ")}
                                            onNavigate={() => { onClose(); navigateDirectory("properties", p.id); }}
                                            onUnlink={() => unlinkProperty(p.id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <Separator className="my-6" />

                        {/* Claims Section */}
                        <div>
                            <div className="flex items-center justify-between flex-wrap mb-3">
                                <h4 className="text-sm font-semibold flex items-center gap-2">
                                    <FileText className="h-4 w-4" /> Claims ({organizationClaims.length})
                                </h4>
                                <LinkEntityPopover
                                    existingIds={organizationClaims.map((co) => co.claimId)}
                                    onSearch={searchClaims}
                                    onSelect={(id) => linkClaim(id)}
                                    placeholder="Search claims..."
                                    emptyMessage="No matching claims"
                                    noItemsMessage="No claims yet"
                                    icon={FileText}
                                />
                            </div>
                            {claimsLoading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-8 w-full" />
                                </div>
                            ) : organizationClaims.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No linked claims</p>
                            ) : (
                                <div className="space-y-2">
                                    {organizationClaims.map((co) => (
                                        <LinkedEntityRow
                                            key={co.id}
                                            icon={FileText}
                                            label={[co.insuredName, co.claimNumber].filter(Boolean).join(" – ") || `Claim #${co.claimId}`}
                                            onNavigate={() => { onClose(); setLocation(`/claims/${co.claimId}`); }}
                                            onUnlink={() => unlinkClaim(co.claimId)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}

